import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.scss';
import { useAuth } from '../../services/AuthContext.tsx';
import { getEstabelecimentos, getUsuarios, deleteEstabelecimento, deleteUsuario } from '../../services/api.tsx';

/* ─── Ícones inline (mesmo padrão da Home) ────────────────────────────── */
const Icon = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  store: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9,22 9,12 15,12 15,22" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  trendUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" /><polyline points="17,6 23,6 23,12" />
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16,17 21,12 16,7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <polyline points="3,6 5,6 21,6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  mapPin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="3" />
    </svg>
  ),
  loading: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24" className={styles?.spinner}>
      <circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="10" />
    </svg>
  ),
};

/* ─── Interfaces ──────────────────────────────────────────────────────── */
interface Empresa {
  id: number;
  nome: string;
  categorias?: { id: number; nomeCategoria: string }[];
  avaliacao: number;
  descricao: string;
  endereco: string;
  latitude?: any;
  longitude?: any;
  fotos?: string[];
}

interface Usuario {
  id: number;
  nome: string;
  email: string;
  isAdmin?: boolean;
}

type Tab = 'dashboard' | 'empresas' | 'usuarios';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logoutSession } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [searchEmpresas, setSearchEmpresas] = useState('');
  const [searchUsuarios, setSearchUsuarios] = useState('');
  const [animateChart, setAnimateChart] = useState(false);

  /* ─── State dos dados da API ─────────────────────────────────────────── */
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingUsuarios, setLoadingUsuarios] = useState(true);
  const [errorEmpresas, setErrorEmpresas] = useState('');
  const [errorUsuarios, setErrorUsuarios] = useState('');

  /* ─── Fetch data ─────────────────────────────────────────────────────── */
  const fetchEmpresas = useCallback(async () => {
    setLoadingEmpresas(true);
    setErrorEmpresas('');
    try {
      const data = await getEstabelecimentos();
      setEmpresas(Array.isArray(data) ? data : []);
    } catch {
      setErrorEmpresas('Não foi possível carregar os estabelecimentos.');
    } finally {
      setLoadingEmpresas(false);
    }
  }, []);

  const fetchUsuarios = useCallback(async () => {
    setLoadingUsuarios(true);
    setErrorUsuarios('');
    try {
      const data = await getUsuarios();
      setUsuarios(Array.isArray(data) ? data : []);
    } catch {
      setErrorUsuarios('Não foi possível carregar os usuários.');
    } finally {
      setLoadingUsuarios(false);
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
    fetchUsuarios();
    const timer = setTimeout(() => setAnimateChart(true), 300);
    return () => clearTimeout(timer);
  }, [fetchEmpresas, fetchUsuarios]);

  /* ─── Handlers ───────────────────────────────────────────────────────── */
  const handleLogout = () => {
    logoutSession();
    navigate('/');
  };

  const handleDeleteEmpresa = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja remover este estabelecimento?')) return;
    try {
      await deleteEstabelecimento(id);
      setEmpresas((prev) => prev.filter((e) => e.id !== id));
    } catch {
      alert('Erro ao remover estabelecimento.');
    }
  };

  const handleDeleteUsuario = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja remover este usuário?')) return;
    try {
      await deleteUsuario(id);
      setUsuarios((prev) => prev.filter((u) => u.id !== id));
    } catch {
      alert('Erro ao remover usuário.');
    }
  };

  /* ─── Filtros ────────────────────────────────────────────────────────── */
  const filteredEmpresas = empresas.filter((e) => {
    const s = searchEmpresas.toLowerCase();
    const categoria = e.categorias?.map((c) => c.nomeCategoria).join(' ') || '';
    return (
      e.nome.toLowerCase().includes(s) ||
      e.endereco.toLowerCase().includes(s) ||
      e.descricao.toLowerCase().includes(s) ||
      categoria.toLowerCase().includes(s)
    );
  });

  const filteredUsuarios = usuarios.filter((u) => {
    const s = searchUsuarios.toLowerCase();
    return u.nome.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
  });

  /* ─── Métricas dinâmicas ─────────────────────────────────────────────── */
  const avgAvaliacao = empresas.length > 0
    ? (empresas.reduce((acc, e) => acc + (e.avaliacao || 0), 0) / empresas.length).toFixed(1)
    : '0.0';

  const distributionData = [
    { label: 'Restaurantes', pct: 52, color: '#ea580c' },
    { label: 'Hotéis', pct: 28, color: '#0891b2' },
    { label: 'Pousadas', pct: 14, color: '#7c3aed' },
    { label: 'Cultura', pct: 6, color: '#16a34a' },
  ];

  const storedViews = localStorage.getItem('@Sergipanidade:site_views');
  const pageViews = storedViews ? parseInt(storedViews, 10).toLocaleString('pt-BR') : '0';

  const metricsData = [
    { label: 'Total de Usuários', value: loadingUsuarios ? '...' : String(usuarios.length), icon: Icon.users, color: '#0891b2', bgColor: '#ecfeff' },
    { label: 'Estabelecimentos', value: loadingEmpresas ? '...' : String(empresas.length), icon: Icon.store, color: '#ea580c', bgColor: '#fff7ed' },
    { label: 'Visualizações do site', value: pageViews, icon: Icon.eye, color: '#7c3aed', bgColor: '#f5f3ff' },
    { label: 'Avaliação Média', value: loadingEmpresas ? '...' : avgAvaliacao, icon: Icon.star, color: '#f59e0b', bgColor: '#fffbeb' },
  ];

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'ativo': return styles.statusAtivo;
      case 'pendente': return styles.statusPendente;
      case 'inativo': return styles.statusInativo;
      default: return styles.statusAtivo;
    }
  };

  const avatarColors = ['#ea580c','#0891b2','#7c3aed','#16a34a','#f59e0b','#dc2626'];

  /* ─── Loading component ──────────────────────────────────────────────── */
  const LoadingState = () => (
    <div className={styles.loadingState}>
      <div className={styles.spinner} />
      <span>Carregando dados...</span>
    </div>
  );

  const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className={styles.errorState}>
      <span>{message}</span>
      <button onClick={onRetry} className={styles.retryBtn}>Tentar novamente</button>
    </div>
  );

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className={styles.layout}>

      {/* ── SIDEBAR ──────────────────────────────────────────────────── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarBrand}>
          <div className={styles.brandLogo}>
            <img src='/assets/logo-simplificada.png' alt="Sergipanidade" className={styles.brandLogoImg} />
          </div>
          <span className={styles.brandText}>ergipanidade</span>
          <span className={styles.brandBadge}>Admin</span>
        </div>

        <nav className={styles.sidebarNav}>
          {(['dashboard', 'empresas', 'usuarios'] as Tab[]).map((tab) => (
            <button key={tab}
              className={`${styles.navItem} ${activeTab === tab ? styles.navItemActive : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'dashboard' ? Icon.dashboard : tab === 'empresas' ? Icon.store : Icon.users}
              <span>{tab === 'dashboard' ? 'Dashboard' : tab === 'empresas' ? 'Empresas' : 'Usuários'}</span>
            </button>
          ))}
        </nav>

        <button className={styles.logoutBtn} onClick={handleLogout}>
          {Icon.logout}
          <span>Sair</span>
        </button>
      </aside>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────── */}
      <main className={styles.main}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.pageTitle}>
              {activeTab === 'dashboard' && 'Painel Administrativo'}
              {activeTab === 'empresas' && 'Gestão de Empresas'}
              {activeTab === 'usuarios' && 'Gestão de Usuários'}
            </h1>
            <p className={styles.pageSubtitle}>
              {activeTab === 'dashboard' && 'Visão geral das métricas da plataforma'}
              {activeTab === 'empresas' && 'Gerencie os estabelecimentos cadastrados'}
              {activeTab === 'usuarios' && 'Gerencie os usuários da plataforma'}
            </p>
          </div>
          <div className={styles.headerActions}>
            <span className={styles.dateLabel}>
              {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </header>

        {/* ── DASHBOARD TAB ─────────────────────────────────────────── */}
        {activeTab === 'dashboard' && (
          <div className={styles.dashContent}>
            <div className={styles.metricsGrid}>
              {metricsData.map((m) => (
                <div key={m.label} className={styles.metricCard}>
                  <div className={styles.metricIconWrap} style={{ background: m.bgColor, color: m.color }}>
                    {m.icon}
                  </div>
                  <div className={styles.metricInfo}>
                    <span className={styles.metricLabel}>{m.label}</span>
                    <span className={styles.metricValue}>{m.value}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Distribuição por categoria */}
            <div className={styles.chartsRow}>
              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <h3>Distribuição por Categoria</h3>
                </div>
                {loadingEmpresas ? <LoadingState /> : (
                  <div className={styles.distributionList}>
                    {distributionData.map((d) => (
                      <div key={d.label} className={styles.distItem}>
                        <div className={styles.distLabel}>
                          <span className={styles.distDot} style={{ background: d.color }} />
                          <span>{d.label}</span>
                        </div>
                        <div className={styles.distBarTrack}>
                          <div className={styles.distBar} style={{ width: animateChart ? `${d.pct}%` : '0%', background: d.color }} />
                        </div>
                        <span className={styles.distPct}>{d.pct}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Tabela recente */}
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3>Empresas Recentes</h3>
                <button className={styles.viewAllBtn} onClick={() => setActiveTab('empresas')}>Ver todas →</button>
              </div>
              {loadingEmpresas ? <LoadingState /> : errorEmpresas ? (
                <ErrorState message={errorEmpresas} onRetry={fetchEmpresas} />
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead><tr><th>Nome</th><th>Categorias</th><th>Avaliação</th><th>Endereço</th></tr></thead>
                    <tbody>
                      {empresas.slice(0, 5).map((e) => (
                        <tr key={e.id}>
                          <td className={styles.tdName}>{e.nome}</td>
                          <td>{e.categorias?.map((c) => c.nomeCategoria).join(', ') || '—'}</td>
                          <td><span className={styles.ratingBadge}>{Icon.star} {e.avaliacao?.toFixed(1) ?? '—'}</span></td>
                          <td><span className={styles.cityBadge}>{Icon.mapPin} {e.endereco || '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── EMPRESAS TAB ──────────────────────────────────────────── */}
        {activeTab === 'empresas' && (
          <div className={styles.dashContent}>
            <div className={styles.toolbarCard}>
              <div className={styles.searchWrap}>
                {Icon.search}
                <input type="text" placeholder="Buscar empresa por nome, categoria ou endereço..."
                  value={searchEmpresas} onChange={(e) => setSearchEmpresas(e.target.value)} />
              </div>
              <span className={styles.resultCount}>{filteredEmpresas.length} resultados</span>
            </div>

            {loadingEmpresas ? <LoadingState /> : errorEmpresas ? (
              <ErrorState message={errorEmpresas} onRetry={fetchEmpresas} />
            ) : (
              <div className={styles.tableCard}>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead><tr><th>#</th><th>Nome</th><th>Categorias</th><th>Endereço</th><th>Avaliação</th><th>Ações</th></tr></thead>
                    <tbody>
                      {filteredEmpresas.map((e) => (
                        <tr key={e.id}>
                          <td className={styles.tdId}>{e.id}</td>
                          <td className={styles.tdName}>{e.nome}</td>
                          <td>{e.categorias?.map((c) => c.nomeCategoria).join(', ') || '—'}</td>
                          <td><span className={styles.cityBadge}>{Icon.mapPin} {e.endereco || '—'}</span></td>
                          <td><span className={styles.ratingBadge}>{Icon.star} {e.avaliacao?.toFixed(1) ?? '—'}</span></td>
                          <td>
                            <div className={styles.actionBtns}>
                              <button className={styles.actionBtn} title="Editar">{Icon.edit}</button>
                              <button className={`${styles.actionBtn} ${styles.actionDanger}`} title="Remover"
                                onClick={() => handleDeleteEmpresa(e.id)}>{Icon.trash}</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── USUÁRIOS TAB ──────────────────────────────────────────── */}
        {activeTab === 'usuarios' && (
          <div className={styles.dashContent}>
            <div className={styles.toolbarCard}>
              <div className={styles.searchWrap}>
                {Icon.search}
                <input type="text" placeholder="Buscar usuário por nome ou e-mail..."
                  value={searchUsuarios} onChange={(e) => setSearchUsuarios(e.target.value)} />
              </div>
              <span className={styles.resultCount}>{filteredUsuarios.length} resultados</span>
            </div>

            {loadingUsuarios ? <LoadingState /> : errorUsuarios ? (
              <ErrorState message={errorUsuarios} onRetry={fetchUsuarios} />
            ) : (
              <div className={styles.tableCard}>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead><tr><th>#</th><th>Nome</th><th>E-mail</th><th>Admin</th><th>Ações</th></tr></thead>
                    <tbody>
                      {filteredUsuarios.map((u, i) => (
                        <tr key={u.id}>
                          <td className={styles.tdId}>{u.id}</td>
                          <td className={styles.tdName}>
                            <div className={styles.userCell}>
                              <div className={styles.userAvatar} style={{ background: avatarColors[i % avatarColors.length] }}>
                                {u.nome?.[0]?.toUpperCase() || '?'}
                              </div>
                              {u.nome}
                            </div>
                          </td>
                          <td>{u.email}</td>
                          <td>
                            <span className={`${styles.statusBadge} ${u.isAdmin ? styles.statusAtivo : styles.statusInativo}`}>
                              {u.isAdmin ? 'Sim' : 'Não'}
                            </span>
                          </td>
                          <td>
                            <div className={styles.actionBtns}>
                              <button className={styles.actionBtn} title="Editar">{Icon.edit}</button>
                              <button className={`${styles.actionBtn} ${styles.actionDanger}`} title="Remover"
                                onClick={() => handleDeleteUsuario(u.id)}>{Icon.trash}</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminDashboard;
